import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { useAuth } from "@/lib/auth-context";
import { useFeatures, type Features } from "@/lib/features";

/**
 * Route guard for a surface flag.
 *
 * Hiding a nav entry is cosmetic: a bookmark, a pasted link or browser history
 * still reaches the page. This closes that gap so a disabled surface is
 * genuinely absent from the product rather than merely hard to find.
 *
 * It is *not* a security boundary. The underlying API stays open — the dossier
 * flows depend on the same person endpoints — so anything that must be denied
 * belongs in `require_permission` on the backend. This only decides what the
 * product presents.
 */
export function FeatureRoute({
  feature,
  children,
}: {
  feature: keyof Features;
  children: ReactNode;
}) {
  const { isLoading } = useAuth();
  const features = useFeatures();

  // The flag rides on the organization, which loads with the session. Deciding
  // before it arrives would bounce a legitimate visitor off their own page.
  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // `replace` so Back does not land on the blocked URL and bounce again.
  return features[feature] ? <>{children}</> : <Navigate to="/" replace />;
}
